import { StorylineSettingsClients } from '@ms/yammer-graphql';
import queryStorylineSettings from '@ms/yammer-graphql/dist/operations/queryStorylineSettings.graphql';

import {
  GraphqlApiTreatmentRecord,
  ManagedApiLifecycle,
  getGraphqlApiRequest,
  getTreatmentsExtensionForGraphqlBody,
  managedApiAction,
} from '../../api';
import { normalizeStorylineSettings } from '../../normalization/graphql/normalizeStorylineSettings';
import { Experiment } from '../../state/ecsExperiments/types';
import { reportError } from '../../telemetry';
import { PromiseThunkAction } from '../../types';

import {
  StorylineSettingsLoadFulfilledAction,
  StorylineSettingsLoadPendingAction,
  StorylineSettingsLoadRejectedAction,
} from './actions.types';
import { getStorylineSettingsStatus } from './selectors';

interface StorylineSettingsResponse {
  readonly data: StorylineSettingsClients;
}

const loadStorylineSettingsPending = (): StorylineSettingsLoadPendingAction => ({
  type: 'STORYLINE_SETTINGS.LOAD_PENDING',
});

const loadStorylineSettingsFulfilled = (
  payload: StorylineSettingsLoadFulfilledAction['payload']
): StorylineSettingsLoadFulfilledAction => ({
  type: 'STORYLINE_SETTINGS.LOAD_FULFILLED',
  payload,
});

const loadStorylineSettingsRejected = (error: unknown): StorylineSettingsLoadRejectedAction => ({
  type: 'STORYLINE_SETTINGS.LOAD_REJECTED',
  payload: {
    error,
  },
  error: true,
});

const loadStorylineSettingsRequest = (treatments: GraphqlApiTreatmentRecord[]) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'StorylineSettingsClients',
      query: queryStorylineSettings,
    },
    treatments,
  });

const loadStorylineSettingsLifecycle: ManagedApiLifecycle<StorylineSettingsResponse> = {
  pending: () => loadStorylineSettingsPending(),
  fulfilled: (rawFeatureAccessSettings) => {
    const payload = normalizeStorylineSettings(rawFeatureAccessSettings);

    return loadStorylineSettingsFulfilled(payload);
  },
  rejected: (error) => loadStorylineSettingsRejected(error),
};

type Load = () => PromiseThunkAction<void>;
export const load: Load = () => async (dispatch, getState) => {
  const state = getState();
  const status = getStorylineSettingsStatus(state);
  const treatments = getTreatmentsExtensionForGraphqlBody({
    state,
    experiments: [
      Experiment.OverrideAndDisableStorylineAnnouncements,
      Experiment.VivaEngagePremium,
      Experiment.UserStoryline,
      Experiment.Stories,
      Experiment.StorylinePerUserControl,
    ],
  });

  if (status === 'pending') {
    return;
  }

  const action = managedApiAction({
    request: loadStorylineSettingsRequest(treatments),
    lifecycle: loadStorylineSettingsLifecycle,
  });

  try {
    await dispatch(action);
  } catch (error) {
    reportError({ error });
  }
};
